import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { API_BASE_URL, AuthError, apiFetch } from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import type { components } from '@/api/schema'

export type LessonProgress = components['schemas']['TrainingProgressRead']
export type TeamMemberProgress = components['schemas']['TeamMemberProgressRead']

export const learnKeys = {
  mine: ['learn', 'progress'] as const,
  team: ['learn', 'team'] as const,
}

function useLearnRequest() {
  const { getAccessToken } = useAuth()
  return async function request<T>(path: string, init?: { method?: string; json?: unknown }): Promise<T> {
    const token = await getAccessToken()
    if (!token) throw new AuthError(401, 'Failed to acquire access token')
    const response = await apiFetch(`${API_BASE_URL}/dashboard/learn${path}`, {
      method: init?.method ?? 'GET',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: init?.json === undefined ? undefined : JSON.stringify(init.json),
    })
    const body: unknown = await response.json().catch(() => null)
    if (!response.ok) throw body ?? new Error(`Request failed with ${response.status}`)
    return body as T
  }
}

/** The signed-in person's lesson results, by lesson key. */
export function useMyProgress() {
  const request = useLearnRequest()
  return useQuery({
    queryKey: learnKeys.mine,
    queryFn: () => request<LessonProgress[]>('/progress'),
    select: (rows) => new Map(rows.map((row) => [row.lesson_key, row])),
  })
}

export function useTeamProgress() {
  const request = useLearnRequest()
  return useQuery({ queryKey: learnKeys.team, queryFn: () => request<TeamMemberProgress[]>('/team') })
}

/** Finish a lesson and/or save a quiz score; the best score is kept. */
export function useRecordProgress() {
  const request = useLearnRequest()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ lessonKey, ...body }: { lessonKey: string; completed?: boolean; quiz_score?: number }) =>
      request<LessonProgress>(`/progress/${lessonKey}`, { method: 'PUT', json: body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['learn'] })
    },
  })
}
